import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, ArrowUpDown, Calendar, User, List, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, isValid } from "date-fns";
import { ro } from "date-fns/locale";
import { ADMIN_ROUTES, USER_ROUTES } from "@/routes";
import { usePageMeta } from "@/hooks/usePageMeta";
import apiClient from "@/services/api/client";
import type { Event } from "@/services/api/events";
import type { PlanType } from "@/models/Plan";

type SortField = "name" | "date" | "created" | "user";
type SortDirection = "asc" | "desc";
type StatusFilter = "all" | "active" | "inactive";
type PlanFilter = "all" | PlanType;
type ViewMode = "list" | "calendar";

type AdminListedEvent = Event & {
  user?: { user_id: number; user_email: string; user_name: string } | null;
  guests_count?: number;
  confirmed_guests_count?: number;
  gifts_active_count?: number;
};

interface EventsListingResponse {
  items: AdminListedEvent[];
  cursor: string | null;
  hasMore: boolean;
}

export default function EventsListing() {
  const { t } = useTranslation();
  const _locale = useRouteLocale();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const currentLocale = _locale || "ro";
  usePageMeta({
    title: t("management.eventsMetaTitle"),
    description: t("management.eventsListing"),
    locale: currentLocale,
    path: ADMIN_ROUTES.events(currentLocale),
    noindex: true,
  });

  // Redirect if not administrator
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/${currentLocale}/management/login`, { replace: true });
    } else if (!user.user_admin) {
      navigate(USER_ROUTES.events(currentLocale), { replace: true });
    } else {
      setCheckingPermissions(false);
    }
  }, [user, authLoading, navigate, currentLocale]);

  const handleViewEvent = async (event: AdminListedEvent) => {
    const slugOrUuid = event.url || event.uuid;
    try {
      const { data } = await apiClient.post<{ token: string }>(`/events/${event.uuid}/preview-token`);
      if (data?.token) {
        navigate(`/@${slugOrUuid}?preview=${encodeURIComponent(data.token)}`);
        return;
      }
    } catch {
      // Fallback to direct public route if preview token issuance fails
    }
    navigate(`/@${slugOrUuid}`);
  };

  const [events, setEvents] = useState<AdminListedEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("created");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [planFilter, setPlanFilter] = useState<PlanFilter>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!checkingPermissions) {
      loadEvents();
    }
  }, [checkingPermissions, sortField, sortDirection, statusFilter, planFilter]);

  const loadEvents = async (loadMore: boolean = false) => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        per_page: 50,
        sort: sortField,
        direction: sortDirection,
      };

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      if (planFilter !== "all") {
        params.plan = planFilter;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (loadMore && cursor) {
        params.cursor = cursor;
      }

      const response = await apiClient.get<EventsListingResponse>(
        `/management/events`,
        { params }
      );

      if (loadMore) {
        setEvents((prev) => [...prev, ...response.data.items]);
      } else {
        setEvents(response.data.items);
      }

      setCursor(response.data.cursor || null);
      setHasMore(response.data.hasMore || false);
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(
        errorMessage ||
          t("events.loadError")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleSearch = () => {
    loadEvents();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "PPp", { locale: currentLocale === "ro" ? ro : undefined });
    } catch {
      return dateString;
    }
  };

  // Group events by month then by date for calendar view
  const eventsByMonthAndDate = (): Array<{ monthKey: string; monthLabel: string; dates: Array<{ dateKey: string; dateLabel: string; events: Event[] }> }> => {
    const byMonth = new Map<string, Map<string, Event[]>>();
    const noDate: Event[] = [];
    for (const event of events) {
      const d = event.date ? parseISO(event.date) : null;
      if (!event.date || !d || !isValid(d)) {
        noDate.push(event);
        continue;
      }
      const monthKey = format(startOfMonth(d), "yyyy-MM");
      const dateKey = format(d, "yyyy-MM-dd");
      if (!byMonth.has(monthKey)) byMonth.set(monthKey, new Map());
      const dateMap = byMonth.get(monthKey)!;
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, []);
      dateMap.get(dateKey)!.push(event);
    }
    const months: Array<{ monthKey: string; monthLabel: string; dates: Array<{ dateKey: string; dateLabel: string; events: Event[] }> }> = [];
    const dateLocale = currentLocale === "ro" ? ro : undefined;
    for (const monthKey of Array.from(byMonth.keys()).sort()) {
      const dateMap = byMonth.get(monthKey)!;
      const dates = Array.from(dateMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, evs]) => ({
          dateKey,
          dateLabel: format(parseISO(dateKey), "PP", { locale: dateLocale }),
          events: evs,
        }));
      months.push({
        monthKey,
        monthLabel: format(parseISO(monthKey + "-01"), "MMMM yyyy", { locale: dateLocale }),
        dates,
      });
    }
    if (noDate.length > 0) {
      months.push({
        monthKey: "_nodate",
        monthLabel: t("events.noDate") || "No date",
        dates: [{ dateKey: "_", dateLabel: "—", events: noDate }],
      });
    }
    return months;
  };

  if (checkingPermissions) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
        <AdminHeader currentLocale={currentLocale} active="events" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t("management.checkingPermissions")}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
      <AdminHeader currentLocale={currentLocale} active="events" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">
            {t("management.eventsListing")}
          </h2>
          <p className="text-muted-foreground">
            {t("management.eventsListingDescription", {
            })}
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex-1 flex gap-2">
                <Input
                  placeholder={t("management.searchEvents", {
                  })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="max-w-sm"
                />
                <Button onClick={handleSearch} variant="outline">
                  <Search className="h-4 w-4 mr-2" />
                  {t("common.search")}
                </Button>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex rounded-md border p-0.5">
                  <Button
                    variant={viewMode === "list" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                  >
                    <List className="h-4 w-4 mr-1.5" />
                    {t("management.listView")}
                  </Button>
                  <Button
                    variant={viewMode === "calendar" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("calendar")}
                  >
                    <CalendarDays className="h-4 w-4 mr-1.5" />
                    {t("management.calendarView")}
                  </Button>
                </div>
                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as StatusFilter)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("common.all")}
                    </SelectItem>
                    <SelectItem value="active">
                      {t("common.active")}
                    </SelectItem>
                    <SelectItem value="inactive">
                      {t("common.inactive")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={planFilter}
                  onValueChange={(value) => setPlanFilter(value as PlanFilter)}
                >
                  <SelectTrigger className="w-[180px]" data-testid="plan-filter-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t("management.allPlans")}
                    </SelectItem>
                    <SelectItem value="free">
                      {t("management.eventsFree")}
                    </SelectItem>
                    <SelectItem value="standard">
                      {t("management.eventsStandard")}
                    </SelectItem>
                    <SelectItem value="premium">
                      {t("management.eventsPremium")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading && events.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>
                  {t("management.noEvents")}
                </p>
              </div>
            ) : viewMode === "calendar" ? (
              <div className="space-y-8">
                {eventsByMonthAndDate().map(({ monthKey, monthLabel, dates }) => (
                  <div key={monthKey}>
                    <h3 className="text-lg font-semibold mb-3 text-muted-foreground capitalize">
                      {monthLabel}
                    </h3>
                    <div className="space-y-4">
                      {dates.map(({ dateKey, dateLabel, events: dayEvents }) => (
                        <div key={dateKey} className="rounded-md border p-3">
                          <p className="text-sm font-medium mb-2">{dateLabel}</p>
                          <ul className="space-y-1.5">
                            {dayEvents.map((event) => (
                              <li
                                key={event.uuid}
                                className="flex items-center justify-between gap-2"
                              >
                                <span className="font-medium truncate">
                                  {event.name}
                                </span>
                                <div className="flex items-center gap-2 shrink-0">
                                  <Badge
                                    variant={event.active ? "default" : "secondary"}
                                    className="text-xs"
                                  >
                                    {event.active
                                      ? t("common.active")
                                      : t("common.inactive")}
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewEvent(event)}
                                  >
                                    {t("common.view")}
                                  </Button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 -ml-3"
                            onClick={() => handleSort("name")}
                          >
                            {t("events.name")}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 -ml-3"
                            onClick={() => handleSort("user")}
                          >
                            <User className="mr-2 h-4 w-4" />
                            {t("management.user")}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 -ml-3"
                            onClick={() => handleSort("date")}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            {t("events.date")}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead>
                          {t("common.status")}
                        </TableHead>
                        <TableHead>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 -ml-3"
                            onClick={() => handleSort("created")}
                          >
                            {t("management.created")}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow key={event.uuid}>
                          <TableCell className="font-medium">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span>{event.name}</span>
                                <Badge variant="outline" className="text-xs uppercase">
                                  {event.plan || "free"}
                                </Badge>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {event.confirmed_guests_count ?? 0}/{event.guests_count ?? 0} RSVP · {event.gifts_active_count ?? 0} {t("events.gifts")}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {event.user ? (
                              <Link
                                to={ADMIN_ROUTES.managementUsersSearch(currentLocale, event.user.user_email)}
                                data-testid="event-owner-link"
                                title={t("management.viewOwnerInUsers")}
                                className="group flex flex-col rounded-sm hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
                                  {event.user.user_name}
                                </span>
                                <span className="text-xs text-muted-foreground">{event.user.user_email}</span>
                              </Link>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(event.date ?? undefined)}</TableCell>
                          <TableCell>
                            <Badge
                              variant={event.active ? "default" : "secondary"}
                            >
                              {event.active
                                ? t("common.active")
                                : t("common.inactive")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(event.createdDate)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewEvent(event)}
                            >
                              {t("common.view")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {hasMore && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      variant="outline"
                      onClick={() => loadEvents(true)}
                      disabled={loading}
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      {t("management.loadMore")}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

